/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import Heading from '@atlaskit/heading';
import { IconTile } from '@atlaskit/icon';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import CrossIcon from '@atlaskit/icon/core/cross';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import { RovoIcon } from '@atlaskit/logo';
import { Flex, Inline, Text, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { Suggestion } from '../../../aiSuggestionsPluginType';

import { SuggestionCardFooter } from './SuggestionCardFooter';

type SuggestionCardProps = {
	onAccept?: () => void;
	onClose?: () => void;
	onDiscard?: () => void;
	onDismiss?: () => void;
	onNavigate?: (direction: 'up' | 'down') => void;
	onShowSidebar?: () => void;
	suggestion: Suggestion;
};

const styles = cssMap({
	sourceButton: {
		display: 'flex',
		backgroundColor: 'transparent',
		alignItems: 'center',
		paddingTop: token('space.100'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.0'),
		gap: token('space.100'),
		color: token('color.text.subtle'),
	},
	card: {
		position: 'relative',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		boxShadow: token('elevation.shadow.overlay'),
		width: '280px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		boxSizing: 'border-box',
	},
	rovoAuthorContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

export const SuggestionCard: React.ForwardRefExoticComponent<
	SuggestionCardProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, SuggestionCardProps>(function SuggestionCard(
	{ onAccept, onDiscard, onDismiss, onNavigate, onClose, onShowSidebar, suggestion },
	ref,
): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isSourcesExpanded, setIsSourcesExpanded] = React.useState(false);

	const handleSourcesToggle = useCallback(() => {
		setIsSourcesExpanded((previous) => !previous);
	}, []);

	const ChevronUpSmall = useCallback(() => {
		return (
			<ChevronUpIcon
				label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
				size="small"
			/>
		);
	}, [formatMessage]);

	const ChevronDownSmall = useCallback(() => {
		return (
			<ChevronDownIcon
				label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
				size="small"
			/>
		);
	}, [formatMessage]);

	const isInsight = suggestion.actionKind === 'INFORMATIONAL';

	return (
		<div ref={ref} css={[styles.card]}>
			<Flex alignItems="center" justifyContent="space-between">
				<Inline space="space.0" alignBlock="center">
					<IconButton
						icon={ChevronUpSmall}
						label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('up') : undefined}
					/>
					<IconButton
						icon={ChevronDownSmall}
						label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('down') : undefined}
					/>
				</Inline>
				<Inline space="space.050" alignBlock="center">
					{onShowSidebar && (
						<IconButton
							icon={PanelRightIcon}
							label={formatMessage(aiSuggestionsMessages.cardShowSidebarButtonLabel)}
							appearance="subtle"
							spacing="compact"
							onClick={onShowSidebar}
						/>
					)}
					<IconButton
						icon={CrossIcon}
						label={formatMessage(aiSuggestionsMessages.cardCloseButtonLabel)}
						appearance="subtle"
						spacing="compact"
						onClick={onClose}
					/>
				</Inline>
			</Flex>

			<div css={styles.rovoAuthorContainer}>
				<RovoIcon
					label={formatMessage(aiSuggestionsMessages.cardRovoTitle)}
					size="small"
					shouldUseNewLogoDesign
					shouldUseHexLogo
				/>
				<Heading size="xsmall">{formatMessage(aiSuggestionsMessages.cardRovoTitle)}</Heading>
				<Tooltip
					position="top"
					content={formatMessage(aiSuggestionsMessages.cardPrivateTooltipLabelShort)}
					ignoreTooltipPointerEvents
				>
					<IconTile
						icon={LockLockedIcon}
						label={formatMessage(aiSuggestionsMessages.cardPrivateLabel)}
						appearance="gray"
						size="small"
					/>
				</Tooltip>
			</div>
			<Text as="p" color="color.text">
				{suggestion.description}
			</Text>
			{suggestion.reasoning && (
				<Flex alignItems="start" direction="column" gap="space.100">
					<Pressable xcss={styles.sourceButton} onClick={handleSourcesToggle}>
						{isSourcesExpanded
							? formatMessage(aiSuggestionsMessages.cardSourcesHideButtonLabel)
							: formatMessage(aiSuggestionsMessages.cardSourcesShowButtonLabel)}

						{isSourcesExpanded ? (
							<ChevronDownIcon label="" size="small" />
						) : (
							<ChevronRightIcon label="" size="small" />
						)}
					</Pressable>
					{isSourcesExpanded && (
						<Text as="p" color="color.text.subtle">
							{suggestion.reasoning}
						</Text>
					)}
				</Flex>
			)}
			<Flex alignItems="center" justifyContent="space-between" gap="space.100">
				<Inline space="space.050" alignBlock="center">
					{isInsight ? (
						<Button appearance="subtle" onClick={onDismiss}>
							{formatMessage(aiSuggestionsMessages.cardDismissButtonLabel)}
						</Button>
					) : (
						<React.Fragment>
							<Button appearance="primary" onClick={onAccept}>
								{formatMessage(aiSuggestionsMessages.cardAcceptButtonLabel)}
							</Button>
							<Button appearance="subtle" onClick={onDiscard}>
								{formatMessage(aiSuggestionsMessages.cardDiscardButtonLabel)}
							</Button>
						</React.Fragment>
					)}
				</Inline>
			</Flex>
			<SuggestionCardFooter />
		</div>
	);
});
